/**
 * OriginationSection Component
 *
 * Displays asset origination sources with expandable "View All" functionality.
 * Shows first 3 originations by default, expands to show all when viewMore is true.
 * Optionally displays affiliation validation sidebar with verdict and details.
 */

import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import {
  Button,
  CalendarTick as CalendarTickIcon,
  DashedTag,
} from '@praetorian-chariot/ui';
import { SetURLSearchParams } from 'react-router';

import { FinalVerdict } from '@/sections/asset/components/tabs';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

export interface OriginationResult {
  value: string;
  capability?: string;
  visited?: string;
  created?: string;
  key?: string;
  displayName?: string;
}

export interface AssetAffiliationOverview {
  lastVerdict: string;
  verdictParsedName: string;
  on: string;
}

export interface OriginationSectionProps {
  originations: OriginationResult[];
  viewMore: boolean;
  setViewMore: (value: boolean) => void;
  assetAffiliationOverview?: AssetAffiliationOverview | null;
  setSearchParams: SetURLSearchParams;
  /** Tab change handler for local state management (prevents race conditions) */
  onTabChange?: (tab: string) => void;
}

export function OriginationSection({
  originations,
  viewMore,
  setViewMore,
  assetAffiliationOverview,
  setSearchParams,
  onTabChange,
}: OriginationSectionProps) {
  const originationsExists = originations.length > 0;

  return (
    <div className="flex w-full">
      <div className="bg-chariot-background-secondary flex w-full flex-col gap-3 rounded-md p-6">
        <h1 className="flex gap-3 text-lg font-bold">
          Origination
          {originations.length > 3 && (
            <Button
              styleType="rounded"
              size="xs"
              label={viewMore ? 'View Less' : 'View All'}
              onClick={() => {
                setViewMore(!viewMore);
              }}
              endIcon={
                <ChevronDownIcon
                  className={cn('size-4', viewMore && 'rotate-180')}
                />
              }
            />
          )}
        </h1>
        {!originationsExists && <p>-</p>}
        {originationsExists && (
          <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3 text-nowrap">
            {(viewMore ? originations : originations.slice(0, 3)).map(
              (origination, index) => {
                return (
                  <div
                    key={index}
                    className="flex flex-col gap-1 overflow-hidden"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium">
                        {origination.capability
                          ? `${origination.displayName}: ${origination.value}`
                          : origination.displayName || origination.value}
                      </p>
                      <DashedTag
                        label={origination.value}
                        size="xs"
                        className="w-full overflow-hidden [&>span]:truncate"
                      />
                    </div>
                    {origination.created && (
                      <div className="flex items-center gap-2">
                        <CalendarTickIcon className="text-chariot-brand-primary size-4" />
                        <p>First Seen:</p>
                        <p>{formatDate(origination.created)}</p>
                      </div>
                    )}
                    {origination.visited && (
                      <div className="flex items-center gap-2">
                        <CalendarTickIcon className="text-chariot-brand-primary size-4" />
                        <p>Last Seen:</p>
                        <p>{formatDate(origination.visited)}</p>
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        )}
      </div>
      {assetAffiliationOverview && (
        <div className="w-1/4 min-w-[260px] shrink-0 pl-4">
          <h1 className="pb-3 text-lg font-bold">Affiliation Validation</h1>
          <FinalVerdict value={assetAffiliationOverview.lastVerdict} />
          <div className="flex items-center gap-1 pt-2">
            <p className="text-sm text-nowrap">
              {assetAffiliationOverview.verdictParsedName}:
            </p>
            <p className="text-sm font-bold">
              {formatDate(assetAffiliationOverview.on)}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <p className="text-sm text-nowrap">By:</p>
            <p className="text-sm font-bold">affiliation agent</p>
          </div>
          <Button
            className="mt-4"
            label="View Details"
            styleType="rounded"
            endIcon={<ChevronRightIcon className="size-4 stroke-[3px]" />}
            size="xs"
            onClick={() => {
              // Use local state handler if available (prevents race conditions)
              if (onTabChange) {
                onTabChange('history');
                // Also clear historyId from URL
                setSearchParams(searchParam => {
                  searchParam.delete('historyId');
                  return searchParam;
                });
              } else {
                // Fallback for backwards compatibility
                setSearchParams(searchParam => {
                  searchParam.set('assetDrawerTab', 'history');
                  searchParam.delete('historyId');
                  return searchParam;
                });
              }
            }}
          />
        </div>
      )}
    </div>
  );
}
